import { useState, useRef, useEffect } from 'react';
import './RadioPlayer.css';

interface Metadata {
  title: string;
  artist: string;
  artwork?: string;
}

interface IcecastResponse {
  icestats: {
    source: {
      title: string;
      server_name?: string;
      [key: string]: any;
    };
  };
}

interface DeezerTrack {
  title: string;
  artist: {
    name: string;
  };
  album: {
    cover_xl: string;
    cover_big: string;
    cover_medium: string;
  };
}

export default function RadioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [metadata, setMetadata] = useState<Metadata>({
    title: 'Loading...',
    artist: 'LTR Radio',
    artwork: undefined
  });
  const [displayMetadata, setDisplayMetadata] = useState<Metadata>({
    title: 'Loading...',
    artist: 'LTR Radio',
    artwork: undefined
  });
  const audioRef = useRef<HTMLAudioElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const artistRef = useRef<HTMLDivElement>(null);
  const trackInfoRef = useRef<HTMLDivElement>(null);
  const albumArtRef = useRef<HTMLDivElement>(null);
  const streamUrl = 'https://on.ltr.ovh/ltr-test';
  const lastTitleRef = useRef<string>('');
  const isFirstLoadRef = useRef<boolean>(true);

  // Fetch track info from Deezer API using JSONP
  const fetchDeezerMetadata = async (artist: string, title: string): Promise<Metadata | null> => {
    try {
      const query = encodeURIComponent(`${artist} ${title}`);

      // Use JSONP callback approach
      return new Promise((resolve) => {
        const callbackName = `deezer_${Date.now()}`;
        const script = document.createElement('script');

        // Set up callback
        (window as any)[callbackName] = (data: any) => {
          delete (window as any)[callbackName];
          document.body.removeChild(script);

          if (data.data && data.data.length > 0) {
            const track: DeezerTrack = data.data[0];
            resolve({
              title: track.title,
              artist: track.artist.name,
              artwork: track.album.cover_xl || track.album.cover_big
            });
          } else {
            resolve(null);
          }
        };

        // Create script tag
        script.src = `https://api.deezer.com/search?q=${query}&limit=1&output=jsonp&callback=${callbackName}`;
        script.onerror = () => {
          delete (window as any)[callbackName];
          document.body.removeChild(script);
          resolve(null);
        };

        document.body.appendChild(script);

        // Timeout after 5 seconds
        setTimeout(() => {
          if ((window as any)[callbackName]) {
            delete (window as any)[callbackName];
            if (document.body.contains(script)) {
              document.body.removeChild(script);
            }
            resolve(null);
          }
        }, 5000);
      });
    } catch (error) {
      console.error('Failed to fetch from Deezer:', error);
      return null;
    }
  };

  useEffect(() => {
    // Fetch metadata from Icecast endpoint
    const fetchMetadata = async () => {
      try {
        const response = await fetch('https://test.ltr.ovh/np');
        const data: IcecastResponse = await response.json();

        const source = data.icestats?.source;
        if (!source) {
          throw new Error('No source data');
        }

        const rawTitle = source.title || source.server_name || 'Unknown Track';

        // Skip if same as last title
        if (rawTitle === lastTitleRef.current) {
          return;
        }
        lastTitleRef.current = rawTitle;

        // Check if it's a jingle - if so, skip updating metadata
        if (rawTitle.toLowerCase().includes('jingle')) {
          return;
        }

        // Parse "Artist - Title" format
        const parts = rawTitle.split(' - ');
        const artist = parts[0]?.trim() || 'Unknown Artist';
        const title = parts[1]?.trim() || parts[0]?.trim() || 'Unknown Track';

        // Set basic metadata immediately
        setMetadata({
          title,
          artist,
          artwork: undefined
        });

        // Fetch enhanced metadata from Deezer
        const deezerData = await fetchDeezerMetadata(artist, title);
        if (deezerData) {
          setMetadata(deezerData);
        }
      } catch (error) {
        console.error('Failed to fetch metadata:', error);
        setMetadata({
          title: 'Unable to load',
          artist: 'LTR Radio',
          artwork: undefined
        });
      }
    };

    fetchMetadata();
    const interval = setInterval(fetchMetadata, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, []);



  // Crossfade effect when metadata changes
  useEffect(() => {
    // Check if metadata actually changed
    if (
      metadata.title === displayMetadata.title &&
      metadata.artist === displayMetadata.artist &&
      metadata.artwork === displayMetadata.artwork
    ) {
      return;
    }

    // On first load, just update without animation
    if (isFirstLoadRef.current) {
      setDisplayMetadata(metadata);
      isFirstLoadRef.current = false;
      return;
    }

    const fadeOut = () => {
      if (trackInfoRef.current) {
        trackInfoRef.current.style.transition = 'opacity 0.2s ease-in-out';
        trackInfoRef.current.style.opacity = '0';
      }
      if (albumArtRef.current) {
        albumArtRef.current.style.transition = 'opacity 0.2s ease-in-out';
        albumArtRef.current.style.opacity = '0';
      }
    };

    const updateContent = () => {
      setDisplayMetadata(metadata);
      // Immediately start fading in after content update
      setTimeout(() => {
        if (trackInfoRef.current) {
          trackInfoRef.current.style.opacity = '1';
        }
        if (albumArtRef.current) {
          albumArtRef.current.style.opacity = '1';
        }
      }, 50);
    };

    // Fade out, then update content (which triggers fade in)
    fadeOut();
    const updateTimeout = setTimeout(updateContent, 200);

    return () => {
      clearTimeout(updateTimeout);
    };
  }, [metadata, displayMetadata]);

  // Check if text overflows and add scroll animation
  useEffect(() => {
    const checkOverflow = (element: HTMLDivElement | null) => {
      if (!element) return;
      const textElement = element.querySelector('.track-text') as HTMLElement;
      if (!textElement) return;

      const isOverflowing = textElement.scrollWidth > element.clientWidth;
      if (isOverflowing) {
        // Calculate the distance to scroll (negative value to move left)
        const scrollDistance = element.clientWidth - textElement.scrollWidth;
        element.style.setProperty('--scroll-distance', `${scrollDistance}px`);
        element.classList.add('scrolling');
      } else {
        element.classList.remove('scrolling');
      }
    };

    // Small delay to ensure DOM is updated after fade-in
    const timeout = setTimeout(() => {
      checkOverflow(titleRef.current);
      checkOverflow(artistRef.current);
    }, 250);

    return () => clearTimeout(timeout);
  }, [displayMetadata]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleWaiting = () => {
      setIsLoading(true);
    };

    const handlePlaying = () => {
      setIsLoading(false);
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
      setIsLoading(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setIsPlaying(false);
    };

    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      setIsLoading(true);
      audioRef.current.play().catch((error) => {
        console.error('Playback failed:', error);
        setIsLoading(false);
      });
    }
  };

  return (
    <div className="radio-player-container">
      <div className="radio-player">
        {/* Glass background layers */}
        <div className="glass-bg"></div>
        <div className="glass-shine"></div>
        
        {/* Content */}
        <div className="player-content">
          {/* Play/Pause Button */}
          <button
            className="play-button"
            onClick={togglePlay}
            aria-label={isLoading ? 'Loading' : isPlaying ? 'Pause' : 'Play'}
            disabled={isLoading}
          >
            {isLoading ? (
              <svg className="loading-spinner" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray="14 42"
                  opacity="0.8"
                />
              </svg>
            ) : isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor"/>
                <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M8 5.14v13.72L19 12L8 5.14z" fill="currentColor"/>
              </svg>
            )}
          </button>

          {/* Album Art */}
          <div className="album-art" ref={albumArtRef} style={{ opacity: 1 }}>
            {displayMetadata.artwork ? (
              <img src={displayMetadata.artwork} alt="Album art" />
            ) : (
              <div className="album-art-placeholder">
                <i className="ri-music-2-line"></i>
              </div>
            )}
          </div>

          {/* Track Info */}
          <div className="track-info" ref={trackInfoRef} style={{ opacity: 1 }}>
            <div className="track-title" ref={titleRef}>
              <span className="track-text">{displayMetadata.title}</span>
            </div>
            <div className="track-artist" ref={artistRef}>
              <span className="track-text">{displayMetadata.artist}</span>
            </div>
          </div>
        </div>

        {/* Audio element */}
        <audio ref={audioRef} src={streamUrl} preload="none" />
      </div>
    </div>
  );
}

